import { Info } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/shadcn/popover'
import { cn } from '@/lib/utils'
import { useHelpText } from './useHelpText'

/**
 * On-demand explanation for a single setting: an info icon that opens a popover.
 *
 * Click (not hover) opens it, because help texts are sentences — they must stay readable on
 * touch and selectable for copy, which a tooltip cannot do. Renders nothing when the text is
 * missing or still empty, so an only partly authored help catalogue shows no dead triggers.
 */
export function HelpHint({ helpKey, className }: { helpKey: string; className?: string }) {
  const { t } = useTranslation()
  const text = useHelpText(helpKey)
  if (!text) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('help.trigger')}
          className={cn(
            'inline-flex size-4 shrink-0 items-center justify-center rounded-full text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
            className,
          )}
        >
          <Info className="size-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="leading-relaxed text-muted-foreground">
        {text}
      </PopoverContent>
    </Popover>
  )
}
